<template>
  <div class="card bg-white bg-opacity-90">
    <div class="card-body p-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h1 class="mb-0 text-start">Tillage Simulation</h1>
        <span class="badge bg-secondary">
          Decisions:
          {{ currentScenarioState.decisionHistory.length }} /
          {{ currentScenario.maxDecisions }}
        </span>
      </div>
      <!-- Scenario Selector (Full Width) -->
      <!-- SonarQube: Using Bootstrap 5 dropdown with ARIA roles (listbox/option/presentation)
           instead of native <select> for custom styling. This follows Bootstrap's recommended
           accessible dropdown pattern. The label is associated via aria-labelledby on the button. -->
      <div class="mb-4">
        <label
          id="scenario-label"
          class="form-label fw-bold text-start d-block"
        >
          Select Scenario:
        </label>
        <div class="dropdown">
          <button
            class="btn btn-secondary fw-bold dropdown-toggle w-100 d-flex justify-content-between align-items-center"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            aria-haspopup="listbox"
            aria-labelledby="scenario-label"
          >
            <span>
              <i class="bi bi-list-task me-2" aria-hidden="true"></i>
              Scenario {{ currentScenarioIndex + 1 }} -
              {{ currentScenario.title }}
            </span>
          </button>
          <ul
            class="dropdown-menu w-100"
            role="listbox"
            aria-labelledby="scenario-label"
          >
            <li
              v-for="(scenario, index) in scenarios"
              :key="scenario.id"
              role="presentation"
            >
              <button
                class="dropdown-item"
                role="option"
                :class="{ active: currentScenarioIndex === index }"
                :aria-selected="currentScenarioIndex === index"
                @click="switchScenario(index)"
              >
                <i
                  v-if="currentScenarioIndex === index"
                  class="bi bi-check2 me-2"
                  aria-hidden="true"
                ></i>
                <span :class="{ 'ms-4': currentScenarioIndex !== index }">
                  {{ scenario.title }}
                </span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Visualization Card -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <!-- Main Row: DESCRIPTION | ISO | SIDE -->
          <div class="row g-3">
            <!-- Description Panel -->
            <div class="col-12 col-lg-4">
              <div class="description-wrapper position-relative">
                <div
                  class="p-3 bg-light border border-secondary rounded description-panel d-flex flex-column"
                >
                  <h2 class="h5 mb-2 text-start">
                    {{ currentScenario.title }}
                  </h2>
                  <!-- Scrollable content area for description and image -->
                  <div class="flex-grow-1 overflow-auto mb-2">
                    <p class="mb-2 text-start small">
                      {{ currentScenario.description }}
                    </p>
                    <!-- Reference Image (if available for this scenario) -->
                    <div v-if="scenarioReferenceImage" class="mt-2 text-center">
                      <img
                        :src="scenarioReferenceImage"
                        :alt="currentScenario.title + ' field conditions'"
                        class="img-fluid rounded border"
                        style="max-height: 200px; object-fit: cover"
                      />
                    </div>
                  </div>
                  <!-- Decision History -->
                  <div
                    v-if="currentScenarioState.decisionHistory.length > 0"
                    class="mt-auto text-start"
                  >
                    <div
                      class="d-flex justify-content-between align-items-center bg-white border rounded px-2 py-1"
                    >
                      <small class="text-muted fw-bold">Your Decisions:</small>
                      <div class="d-flex gap-2">
                        <button
                          class="btn btn-sm btn-primary"
                          @click="undoLastDecision"
                        >
                          <i
                            class="bi bi-arrow-counterclockwise me-1"
                            aria-hidden="true"
                          ></i
                          >Undo
                        </button>
                        <button
                          class="btn btn-sm btn-primary"
                          @click="resetCurrentScenario"
                        >
                          <i
                            class="bi bi-arrow-repeat me-1"
                            aria-hidden="true"
                          ></i
                          >Reset
                        </button>
                      </div>
                    </div>
                    <div class="d-flex gap-2 flex-wrap mt-1">
                      <span
                        v-for="(
                          decision, index
                        ) in currentScenarioState.decisionHistory"
                        :key="index"
                        class="badge bg-success"
                      >
                        {{ index + 1 }}. {{ decision.buttonText }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Isometric View -->
            <div class="col-12 col-md-6 col-lg-4">
              <div
                class="position-relative bg-light border border-secondary rounded overflow-hidden simulation-square"
              >
                <!-- Loading Overlay -->
                <output
                  v-if="isLoading"
                  class="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-light bg-opacity-75"
                  style="z-index: 10"
                  aria-live="polite"
                >
                  <div class="text-center">
                    <div
                      class="spinner-border text-primary mb-2"
                      aria-hidden="true"
                    ></div>
                    <div class="small text-muted" aria-hidden="true">
                      Updating soil...
                    </div>
                    <span class="visually-hidden"
                      >Loading isometric view, please wait...</span
                    >
                  </div>
                </output>
                <div class="position-absolute top-0 start-0 w-100 h-100">
                  <!-- Alt text is dynamically generated from scenario data -->
                  <img
                    v-if="effectiveIsoImagePath"
                    :src="effectiveIsoImagePath"
                    :alt="isoViewAltText"
                    class="position-absolute top-0 start-0 w-100 h-100"
                    :style="{
                      objectFit: 'contain',
                      filter: isDeadEnd
                        ? 'grayscale(100%) opacity(0.5)'
                        : 'none'
                    }"
                    @load="onImageLoad('iso')"
                  />
                </div>
                <div
                  v-if="!effectiveIsoImagePath && !isLoading"
                  class="position-absolute top-50 start-50 translate-middle text-secondary text-center"
                >
                  <i class="bi bi-box fs-1 d-block mb-2" aria-hidden="true"></i>
                  <small>Isometric View</small>
                </div>
                <!-- Dead-End Overlay -->
                <div
                  v-if="isDeadEnd && !isLoading"
                  class="position-absolute top-50 start-50 translate-middle text-center"
                  style="z-index: 5"
                  role="alert"
                  aria-live="assertive"
                >
                  <div
                    class="bg-warning bg-opacity-90 text-dark rounded px-3 py-2 shadow"
                  >
                    <i
                      class="bi bi-exclamation-triangle me-2"
                      aria-hidden="true"
                    ></i>
                    <strong>Unrealistic combination</strong>
                    <div class="small mt-1">
                      Use Undo or Reset to try different implements
                    </div>
                  </div>
                </div>
                <!-- View Label -->
                <div
                  class="position-absolute top-0 start-0 m-2 badge bg-dark bg-opacity-75"
                  aria-hidden="true"
                >
                  Isometric
                </div>
              </div>
            </div>

            <!-- Side View -->
            <div class="col-12 col-md-6 col-lg-4">
              <div
                class="position-relative bg-light border border-secondary rounded overflow-hidden simulation-square"
              >
                <!-- Loading Overlay -->
                <div
                  v-if="isLoading"
                  class="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-light bg-opacity-75"
                  style="z-index: 10"
                  aria-hidden="true"
                >
                  <div class="text-center">
                    <div
                      class="spinner-border text-primary mb-2"
                      aria-hidden="true"
                    ></div>
                    <div class="small text-muted">Updating soil...</div>
                  </div>
                </div>
                <div class="position-absolute top-0 start-0 w-100 h-100">
                  <!-- Alt text is dynamically generated from scenario data -->
                  <img
                    v-if="effectiveSideImagePath"
                    :src="effectiveSideImagePath"
                    :alt="sideViewAltText"
                    class="position-absolute top-0 start-0 w-100 h-100"
                    :style="{
                      objectFit: 'contain',
                      filter: isDeadEnd
                        ? 'grayscale(100%) opacity(0.5)'
                        : 'none'
                    }"
                    @load="onImageLoad('side')"
                  />
                </div>
                <div
                  v-if="!effectiveSideImagePath && !isLoading"
                  class="position-absolute top-50 start-50 translate-middle text-secondary text-center"
                >
                  <i class="bi bi-box fs-1 d-block mb-2" aria-hidden="true"></i>
                  <small>Side View</small>
                </div>
                <!-- View Label -->
                <div
                  class="position-absolute top-0 start-0 m-2 badge bg-dark bg-opacity-75"
                  aria-hidden="true"
                >
                  Side
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Decision & Outcome Card -->
      <div class="card shadow-sm mb-4">
        <div class="card-body">
          <!-- Decision Buttons -->
          <div
            v-if="
              availableDecisions.length > 0 &&
              currentScenarioState.decisionHistory.length <
                currentScenario.maxDecisions
            "
            class="mb-4"
          >
            <h3 class="h6 mb-2 text-start fw-bold">
              Choose Your Tillage Implement:
              <span
                v-if="currentScenarioState.decisionHistory.length > 0"
                class="text-muted fw-normal"
              >
                (Pass {{ currentScenarioState.decisionHistory.length + 1 }})
              </span>
            </h3>
            <div class="d-flex gap-2 flex-wrap">
              <button
                v-for="decision in availableDecisions"
                :key="decision.id"
                class="btn btn-primary fw-bold flex-fill d-flex align-items-center justify-content-center gap-2"
                @click="makeDecision(decision)"
              >
                <img
                  v-if="getImplementIcon(decision.buttonText)"
                  :src="getImplementIcon(decision.buttonText)"
                  :alt="decision.buttonText + ' icon'"
                  class="implement-icon"
                />
                {{ decision.buttonText }}
              </button>
            </div>
          </div>

          <!-- Outcome Panel -->
          <div
            class="p-3 rounded text-start"
            :class="
              currentScenarioState.lastOutcomeText
                ? 'bg-primary bg-opacity-10 border border-primary'
                : 'bg-light border border-secondary'
            "
          >
            <h3
              class="h6 mb-2 fw-bold"
              :class="
                currentScenarioState.lastOutcomeText
                  ? 'text-primary'
                  : 'text-muted'
              "
            >
              <i class="bi bi-info-circle-fill me-2" aria-hidden="true"></i
              >Outcome
            </h3>
            <p v-if="currentScenarioState.lastOutcomeText" class="mb-0">
              {{ currentScenarioState.lastOutcomeText }}
            </p>
            <p v-else class="mb-0 text-muted fst-italic">
              Select a tillage implement to see the outcome...
            </p>
          </div>
        </div>
      </div>

      <!-- Developer Notes Panel (Development Only - at very bottom) -->
      <div
        v-if="showDeveloperNotes && currentScenarioState.lastDeveloperNotes"
        class="p-4 border border-3 border-danger rounded bg-danger bg-opacity-25 text-start shadow"
      >
        <h3 class="h6 mb-3 fw-bold text-danger">
          <i class="bi bi-code-square" aria-hidden="true"></i> 🔧 DEVELOPER
          NOTES
        </h3>
        <p class="mb-0 fs-6 fw-medium text-dark" style="line-height: 1.6">
          {{ currentScenarioState.lastDeveloperNotes }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
// Import individual scenario files (keeps files manageable, ~800 lines each)
import scenario1 from "@/data/scenario1.json";
import scenario2 from "@/data/scenario2.json";
import scenario3 from "@/data/scenario3.json";

// Import scenario reference images
import scenario3InitialImage from "@/assets/scenarios/scenario3/scenario3-initial.jpg";

// Map scenario IDs to reference images
const scenarioReferenceImages = {
  3: scenario3InitialImage
};

// Import tillage implement icons
import moldboardIcon from "@/assets/moldboard/plow-graphic.svg";
import diskIcon from "@/assets/disk/plow-graphic.svg";
import chiselIcon from "@/assets/chisel/plow-graphic.svg";
import harrowIcon from "@/assets/harrow/plow-graphic.svg";

// Map button text to icon
// Note: Disk and Disk Harrow intentionally share the same icon
// as they represent the same implement with different terminology
const implementIcons = {
  "Moldboard Plow": moldboardIcon,
  Disk: diskIcon,
  "Disk Harrow": diskIcon,
  "Chisel Plow": chiselIcon,
  Harrow: harrowIcon
};

// Combine all scenarios into a single array, sorted by ID
const allScenarios = [scenario1, scenario2, scenario3].sort(
  (a, b) => a.id - b.id
);

// Import all v13-opt optimized WebP images using Vite's glob import
// Naming convention: s<scenarioId>_start_iso.webp, s<scenarioId>_p1_<implement>_iso.webp, etc.
const v13Images = import.meta.glob(
  "../../assets/renderedStills/v13-opt/*.webp",
  {
    eager: true,
    import: "default"
  }
);

// Map decision ID segments to image naming convention
// Handles both formats: "moldboard_plow" (scenario 1) and "moldboard" (scenarios 2/3)
const decisionIdToImageName = {
  // Scenario 1 format (with _plow, _harrow suffixes)
  moldboard_plow: "moldboard",
  disk_plow: "disk",
  disk_harrow: "disk",
  chisel_plow: "chisel",
  // Scenarios 2/3 format (short names) - pass through as-is
  moldboard: "moldboard",
  disk: "disk",
  chisel: "chisel",
  harrow: "harrow"
};

// Filter to only show completed scenarios
// All scenarios now have complete JSON data
const completedScenarioIds = [1, 2, 3];
const availableScenarios = allScenarios.filter((s) =>
  completedScenarioIds.includes(s.id)
);

export default {
  name: "TillageSimulation",
  data() {
    return {
      // Scenario management (filtered to completed scenarios only)
      scenarios: availableScenarios,
      currentScenarioIndex: 0,

      // Per-scenario state (indexed by scenario index)
      scenarioStates: [],

      // Loading state for image transitions
      isLoading: false,
      imagesLoaded: { iso: false, side: false }
    };
  },
  computed: {
    showDeveloperNotes() {
      return import.meta.env.VITE_DEBUG === "true";
    },
    scenarioReferenceImage() {
      return scenarioReferenceImages[this.currentScenario.id] || null;
    },
    currentScenario() {
      return this.scenarios[this.currentScenarioIndex];
    },
    currentScenarioState() {
      return (
        this.scenarioStates[this.currentScenarioIndex] ||
        this.createEmptyState()
      );
    },
    // Get available decisions based on current position in the decision tree
    // For first pass, show scenario.decisions
    // For subsequent passes, traverse the tree via nextDecisions
    availableDecisions() {
      const history = this.currentScenarioState.decisionHistory;

      // First pass: show root decisions
      if (history.length === 0) {
        return this.currentScenario.decisions;
      }

      // Subsequent passes: traverse the tree to find current node's nextDecisions
      let currentNode = history[history.length - 1];

      // Return the next available decisions, or empty if we've reached max or a leaf
      if (currentNode.nextDecisions && currentNode.nextDecisions.length > 0) {
        return currentNode.nextDecisions;
      }

      return []; // No more decisions available (leaf node or max reached)
    },

    // Build base image name from scenario and decision history
    // Uses the image property from the decision JSON, or falls back to computed path
    currentImageBaseName() {
      const scenarioId = this.currentScenario.id;
      const decisions = this.currentScenarioState.decisionHistory;

      if (decisions.length === 0) {
        return `s${scenarioId}_start`;
      }

      // Get the last decision's image property if available
      const lastDecision = decisions[decisions.length - 1];
      if (lastDecision.image) {
        // Extract base name from image property (remove .png extension)
        return lastDecision.image.replace(/\.png$/, "");
      }

      // Fallback: Build path from decision IDs (for backward compatibility)
      // Pattern: s1_p1_moldboard_p2_disk_p3_chisel
      const parts = [`s${scenarioId}`];
      decisions.forEach((d, index) => {
        const passNum = index + 1;
        // First check if the full ID maps directly (scenario 1 style)
        let implementName = decisionIdToImageName[d.id];
        if (!implementName) {
          // Otherwise extract the last segment (scenarios 2/3 cumulative style)
          const idSegments = d.id.split("_");
          const lastSegment = idSegments[idSegments.length - 1];
          implementName = decisionIdToImageName[lastSegment];
          if (!implementName) {
            console.warn(
              `No image name mapping for decision segment: '${lastSegment}' in decision '${d.id}' (scenario ${this.currentScenario.id})`
            );
            implementName = lastSegment;
          }
        }
        parts.push(`p${passNum}_${implementName}`);
      });

      return parts.join("_");
    },

    // Get isometric view image path
    currentIsoImagePath() {
      const baseName = this.currentImageBaseName;
      const moduleKey = `../../assets/renderedStills/v13-opt/${baseName}_iso.webp`;

      if (v13Images[moduleKey]) {
        return v13Images[moduleKey];
      }

      console.warn(`Iso image not found: ${baseName}_iso.webp`);
      return null;
    },

    // Get side view image path
    currentSideImagePath() {
      const baseName = this.currentImageBaseName;
      const moduleKey = `../../assets/renderedStills/v13-opt/${baseName}_side.webp`;

      if (v13Images[moduleKey]) {
        return v13Images[moduleKey];
      }

      console.warn(`Side image not found: ${baseName}_side.webp`);
      return null;
    },

    // Detect dead-end state (no image for current path)
    isDeadEnd() {
      // Only applies when we have decisions but no image
      const hasDecisions = this.currentScenarioState.decisionHistory.length > 0;
      const noIsoImage = !this.currentIsoImagePath;
      const noSideImage = !this.currentSideImagePath;
      const result = hasDecisions && noIsoImage && noSideImage;
      if (result) {
        console.log("DEAD END DETECTED", {
          hasDecisions,
          noIsoImage,
          noSideImage
        });
      }
      return result;
    },

    // Fallback to scenario start image for dead-end states
    fallbackIsoImagePath() {
      const scenarioId = this.currentScenario.id;
      const moduleKey = `../../assets/renderedStills/v13-opt/s${scenarioId}_start_iso.webp`;
      return v13Images[moduleKey] || null;
    },

    fallbackSideImagePath() {
      const scenarioId = this.currentScenario.id;
      const moduleKey = `../../assets/renderedStills/v13-opt/s${scenarioId}_start_side.webp`;
      return v13Images[moduleKey] || null;
    },

    // Effective image paths (use fallback when in dead-end)
    effectiveIsoImagePath() {
      return (
        this.currentIsoImagePath ||
        (this.isDeadEnd ? this.fallbackIsoImagePath : null)
      );
    },

    effectiveSideImagePath() {
      return (
        this.currentSideImagePath ||
        (this.isDeadEnd ? this.fallbackSideImagePath : null)
      );
    },

    // Generate accessible alt text for simulation renders
    // Uses altText from JSON if available, otherwise summarizes from outcomeText
    currentRenderAltText() {
      const scenario = this.currentScenario;
      const decisions = this.currentScenarioState.decisionHistory;

      // Starting state (no decisions made yet)
      if (decisions.length === 0) {
        // Use startingAltText if available in scenario JSON
        if (scenario.startingAltText) {
          return scenario.startingAltText;
        }
        // Fallback: build from scenario title and starting values
        return `${scenario.title} starting conditions: ${scenario.description.split(".")[0]}.`;
      }

      // After decisions: get the last decision's alt text or outcome
      const lastDecision = decisions[decisions.length - 1];

      // Prefer explicit altText if available
      if (lastDecision.altText) {
        return lastDecision.altText;
      }

      // Fallback: create summary from outcomeText
      // Take first two sentences for a concise but informative description
      const outcomeText = lastDecision.outcomeText || "";
      // Split on sentence-ending punctuation, then reconstruct with the delimiter
      // This approach avoids regex backtracking vulnerabilities (ReDoS)
      const parts = outcomeText.split(/(?<=[.!?])\s+/);
      const sentences = parts.length > 0 ? parts : [outcomeText];
      const summary = sentences.slice(0, 2).join(" ").trim();

      // Build descriptive alt text with context
      const passNumber = decisions.length;
      const implementName = lastDecision.buttonText || "tillage implement";
      return `After pass ${passNumber} with ${implementName}: ${summary}`;
    },

    // Alt text for isometric view (adds view context)
    isoViewAltText() {
      return `Isometric view - ${this.currentRenderAltText}`;
    },

    // Alt text for side view (adds view context)
    sideViewAltText() {
      return `Side cross-section view - ${this.currentRenderAltText}`;
    }
  },
  methods: {
    // Get the icon for a tillage implement button
    getImplementIcon(buttonText) {
      return implementIcons[buttonText] || null;
    },

    // Create an empty state object for a scenario
    createEmptyState() {
      return {
        decisionHistory: [],
        lastOutcomeText: "",
        lastDeveloperNotes: ""
      };
    },

    // Initialize all scenarios with empty decision history
    initializeAllScenarios() {
      this.scenarioStates = this.scenarios.map(() => ({
        decisionHistory: [],
        lastOutcomeText: "",
        lastDeveloperNotes: ""
      }));
    },

    // Switch to a different scenario (preserves state)
    switchScenario(index) {
      this.currentScenarioIndex = index;
    },

    // Reset current scenario to initial state
    resetCurrentScenario() {
      this.scenarioStates.splice(this.currentScenarioIndex, 1, {
        decisionHistory: [],
        lastOutcomeText: "",
        lastDeveloperNotes: ""
      });
    },

    // Make a decision and record it
    makeDecision(decision) {
      // Capture current image paths BEFORE updating state
      const previousIsoPth = this.effectiveIsoImagePath;
      const previousSidePath = this.effectiveSideImagePath;

      // Start loading animation
      this.startLoading();

      const state = { ...this.scenarioStates[this.currentScenarioIndex] };

      // Record decision and show outcome
      state.decisionHistory = [...state.decisionHistory, decision];
      state.lastOutcomeText = decision.outcomeText;
      state.lastDeveloperNotes = decision.developerNotes || "";

      // Use splice for Vue reactivity
      this.scenarioStates.splice(this.currentScenarioIndex, 1, state);

      // Check if this leads to a dead-end (no image for the path)
      // Use nextTick to ensure computed properties have updated
      this.$nextTick(() => {
        // Get the new image paths after state update
        const newIsoPath = this.effectiveIsoImagePath;
        const newSidePath = this.effectiveSideImagePath;

        // Force stop loading on dead-end - don't wait for image load
        if (this.isDeadEnd) {
          console.log("Dead-end detected, stopping loading");
          this.isLoading = false;
          return;
        }

        // Also stop loading if image paths haven't changed
        // (browser won't fire @load for already-loaded images)
        if (newIsoPath === previousIsoPth && newSidePath === previousSidePath) {
          console.log("Image paths unchanged, stopping loading immediately");
          this.isLoading = false;
        }
      });
    },

    // Start loading state
    startLoading() {
      this.isLoading = true;
      this.imagesLoaded = { iso: false, side: false };
    },

    // Handle image load event
    onImageLoad(view) {
      const validViews = ["iso", "side"];
      if (!validViews.includes(view)) {
        console.warn(`onImageLoad called with unexpected view: '${view}'`);
        return;
      }
      this.imagesLoaded[view] = true;
      // Only stop loading when both images are loaded
      if (this.imagesLoaded.iso && this.imagesLoaded.side) {
        this.isLoading = false;
      }
    },

    // Undo last decision
    undoLastDecision() {
      const currentState = this.scenarioStates[this.currentScenarioIndex];
      if (currentState.decisionHistory.length === 0) return;

      // Create a copy of state for immutable update
      const state = { ...currentState };
      const newHistory = [...state.decisionHistory];
      newHistory.pop();
      state.decisionHistory = newHistory;

      // Clear outcome text if no decisions remain
      if (state.decisionHistory.length === 0) {
        state.lastOutcomeText = "";
        state.lastDeveloperNotes = "";
      } else {
        // Show the previous decision's outcome
        const previousDecision =
          state.decisionHistory[state.decisionHistory.length - 1];
        state.lastOutcomeText = previousDecision.outcomeText;
        state.lastDeveloperNotes = previousDecision.developerNotes || "";
      }

      // Use splice for Vue reactivity
      this.scenarioStates.splice(this.currentScenarioIndex, 1, state);
    }
  },
  created() {
    this.initializeAllScenarios();
  }
};
</script>

<style scoped>
/* Square aspect ratio for simulation images */
.simulation-square {
  width: 100%;
  padding-bottom: 100%; /* 1:1 Aspect Ratio */
}

/* Description panel matches height of simulation squares on large screens */
@media (min-width: 992px) {
  .description-wrapper {
    width: 100%;
    padding-bottom: 100%; /* Match square aspect ratio */
  }

  .description-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: auto;
  }
}

/* Tillage implement button icons */
.implement-icon {
  width: 1.5em;
  height: 1.5em;
  filter: brightness(0) invert(1); /* Make SVG white to match button text */
}

.form-range {
  cursor: pointer;
}
</style>
